import React, { useState, useEffect } from 'react';
import axios from 'axios';
import Navbar from '../components/Navbar';
import Footer from '../components/Footer';
import ProductFamilyCard from '../components/ProductFamilyCard';
import { useCart } from '../App';
import STATIC_PRODUCTS from '../data/staticProducts';
import { Check, ChevronDown, ChevronUp, Shield, Clock, Award, Zap, Brain, DollarSign, Heart, Target, Users, Instagram, Youtube } from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL + '/api';

// Hero Image URL from user's uploaded file
const HERO_IMAGE = "/hero.jpg";
const SYSTEM_IMAGE_DEFAULT = "/system.jpg";

// Section order is controlled here in code; CMS controls which sections exist / are visible.
const DEFAULT_SECTION_ORDER = ['hero', 'solution', 'products', 'system', 'testimonials', 'cta', 'problem', 'audience', 'bonus', 'faq'];

// Social Icons component
const SocialIcon = ({ platform, size = 20 }) => {
  switch (platform.toLowerCase()) {
    case 'instagram': return <Instagram size={size} />;
    case 'youtube': return <Youtube size={size} />;
    case 'tiktok': return <svg width={size} height={size} viewBox="0 0 24 24" fill="currentColor"><path d="M19.59 6.69a4.83 4.83 0 0 1-3.77-4.25V2h-3.45v13.67a2.89 2.89 0 0 1-5.2 1.74 2.89 2.89 0 0 1 2.31-4.64 2.93 2.93 0 0 1 .88.13V9.4a6.84 6.84 0 0 0-1-.05A6.33 6.33 0 0 0 5 20.1a6.34 6.34 0 0 0 10.86-4.43v-7a8.16 8.16 0 0 0 4.77 1.52v-3.4a4.85 4.85 0 0 1-1-.1z"/></svg>;
    case 'pinterest': return <svg width={size} height={size} viewBox="0 0 24 24" fill="currentColor"><path d="M12 0a12 12 0 0 0-4.37 23.17c-.1-.94-.2-2.38.04-3.4l1.46-6.17s-.37-.75-.37-1.86c0-1.74 1-3.04 2.26-3.04 1.07 0 1.58.8 1.58 1.76 0 1.07-.68 2.67-1.03 4.15-.3 1.24.62 2.25 1.84 2.25 2.21 0 3.9-2.33 3.9-5.7 0-2.98-2.14-5.06-5.2-5.06-3.54 0-5.62 2.66-5.62 5.4 0 1.07.41 2.22.93 2.85.1.12.12.23.08.35l-.35 1.4c-.05.22-.18.27-.4.16-1.49-.7-2.42-2.87-2.42-4.62 0-3.76 2.73-7.21 7.88-7.21 4.14 0 7.35 2.95 7.35 6.88 0 4.1-2.59 7.41-6.18 7.41-1.21 0-2.34-.63-2.73-1.37l-.74 2.83c-.27 1.03-1 2.33-1.49 3.12A12 12 0 1 0 12 0z"/></svg>;
    case 'facebook': return <svg width={size} height={size} viewBox="0 0 24 24" fill="currentColor"><path d="M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z"/></svg>;
    case 'twitter': case 'x': return <svg width={size} height={size} viewBox="0 0 24 24" fill="currentColor"><path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z"/></svg>;
    default: return null;
  }
};

// Group flat products into "families" (ebook + audiobook + premium of one title)
const buildFamilies = (products) => {
  const fams = {};
  products.forEach((p) => {
    const fam = p.family || `solo-${p.id}`;
    if (!fams[fam]) fams[fam] = { family: fam, order: 9999 };
    const type = p.product_type || (p.is_bundle ? 'premium' : 'ebook');
    fams[fam][type] = p;
    if (typeof p.order === 'number') fams[fam].order = Math.min(fams[fam].order, p.order);
  });
  return Object.values(fams)
    .map((f) => {
      const main = f.ebook || f.audiobook || f.premium;
      const isPack = f.family === 'pakiet';
      return {
        family: f.family,
        isPack,
        order: f.order,
        badge: isPack ? '🔥 VIP PAKIET' : '',
        badge2: isPack ? 'Najczęściej wybierany' : '',
        title: isPack ? 'Kompletny pakiet 5 książek' : (f.ebook?.name || main?.name || ''),
        subtitle: isPack ? 'Pełny system zmiany' : (main?.subtitle || ''),
        description: main?.description || '',
        pages: f.ebook?.pages || f.premium?.pages || 0,
        audio_duration: f.audiobook?.audio_duration || f.premium?.audio_duration || '',
        benefits: (f.ebook || main)?.benefits || [],
        ebook: f.ebook,
        audiobook: f.audiobook,
        premium: f.premium,
      };
    })
    .filter((g) => g.ebook || g.audiobook || g.premium)
    .sort((a, b) => a.order - b.order);
};

const LandingPage = () => {
  const [products, setProducts] = useState(STATIC_PRODUCTS);
  const [testimonials, setTestimonials] = useState([]);
  const [socialMedia, setSocialMedia] = useState([]);
  const [cmsSections, setCmsSections] = useState({});
  const [cmsOrder, setCmsOrder] = useState(DEFAULT_SECTION_ORDER);
  const [loading, setLoading] = useState(false);
  const [openFaq, setOpenFaq] = useState(null);
  const { addToCart } = useCart();

  useEffect(() => {
    const fetchData = async () => {
      try {
        const [productsRes, testimonialsRes, socialRes, cmsRes] = await Promise.all([
          axios.get(`${API}/products`),
          axios.get(`${API}/testimonials?visible_only=true`),
          axios.get(`${API}/social`),
          axios.get(`${API}/cms/sections/public`)
        ]);
        const fresh = productsRes.data.filter(p => p.is_active !== false);
        // Start from the full static catalog (has families / types / local covers /
        // volume), overlay live admin data (real id + prices) where the product
        // already exists in the DB. New products (audio/premium/packs) stay static.
        const merged = STATIC_PRODUCTS.map(sp => {
          const m = fresh.find(p => p.name === sp.name);
          if (!m) return sp;
          return {
            ...sp,
            id: m.id,
            regular_price: m.regular_price ?? sp.regular_price,
            sale_price: m.sale_price ?? sp.sale_price,
            is_on_sale: m.is_on_sale ?? sp.is_on_sale,
            description: m.description || m.short_description || sp.description,
          };
        });
        setProducts(merged);
        setTestimonials(testimonialsRes.data);
        setSocialMedia(socialRes.data.filter(s => s.is_active && s.show_in_header));
        const sectionsObj = {};
        cmsRes.data.forEach(s => { sectionsObj[s.section_id] = s; });
        setCmsSections(sectionsObj);
        if (cmsRes.data.length > 0) {
          const cmsIds = cmsRes.data.map(s => s.section_id);
          // Render in our canonical DEFAULT_SECTION_ORDER, limited to the sections
          // the CMS returns (so hidden sections stay hidden); any unknown extras appended.
          const ordered = DEFAULT_SECTION_ORDER.filter(id => cmsIds.includes(id));
          cmsIds.forEach(id => { if (!ordered.includes(id)) ordered.push(id); });
          setCmsOrder(ordered);
        }
      } catch (e) {
        // backend sleeping — static products already shown, no action needed
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, []);

  // Helper to get CMS section data or fallback
  const getCMS = (sectionId, field, fallback) => {
    return cmsSections[sectionId]?.[field] || fallback;
  };

  const getContent = (sectionId, key, fallback) => {
    return cmsSections[sectionId]?.content?.[key] ?? fallback;
  };

  const getImage = (sectionId, key, fallback) => {
    return cmsSections[sectionId]?.images?.[key] || fallback;
  };

  // Get section accent color (falls back to brand green)
  const getAccent = (sectionId) => {
    return cmsSections[sectionId]?.styles?.accent_color || '#10b981';
  };

  // Get section title font size class
  const getTitleSize = (sectionId, fallback = 'text-3xl md:text-4xl') => {
    const size = cmsSections[sectionId]?.styles?.title_size;
    if (!size) return fallback;
    // Map single tailwind class to responsive pair
    const map = { 'text-2xl': 'text-2xl md:text-3xl', 'text-3xl': 'text-3xl md:text-4xl', 'text-4xl': 'text-3xl md:text-4xl', 'text-5xl': 'text-4xl md:text-5xl' };
    return map[size] || fallback;
  };

  const scrollToProducts = () => {
    document.getElementById('produkty')?.scrollIntoView({ behavior: 'smooth' });
  };

  const problemCards = [
    { icon: <Zap size={24} />, title: "Brak spójnego systemu działania", text: "Zaczynasz z motywacją, ale szybko wracasz do starych schematów. Bez systemu nawet najlepsze chęci nie wystarczą." },
    { icon: <DollarSign size={24} />, title: "Ukryte blokady finansowe", text: "Twoje decyzje finansowe często są sterowane przez podświadome przekonania, których nawet nie zauważasz." },
    { icon: <Heart size={24} />, title: "Błędy w komunikacji i zaufaniu", text: "Nawet dobre intencje nie wystarczą, jeśli brakuje zrozumienia mechanizmów budowania silnych relacji." },
    { icon: <Target size={24} />, title: "Spadek energii i koncentracji", text: "Przemęczenie, stres i styl życia stopniowo obniżają Twoją wydajność, zanim zdążysz to świadomie zauważyć." },
    { icon: <Brain size={24} />, title: "Niewidzialne mechanizmy psychologiczne", text: "Większość decyzji podejmujesz automatycznie — a bez zrozumienia tych procesów trudno przejąć pełną kontrolę." }
  ];

  const solutionBullets = [
    { title: "Autopilot skutecznego działania", text: "Zbudujesz nawyki i rutyny, które pracują na Twoją korzyść każdego dnia." },
    { title: "Świadome decyzje finansowe", text: "Zrozumiesz mechanizmy, które wpływają na Twoje wybory i zaczniesz lepiej zarządzać pieniędzmi." },
    { title: "Mentalna odporność i fokus", text: "Poznasz techniki pracy z rozproszeniem, stresem i wewnętrznym oporem." },
    { title: "Większa kontrola nad sobą", text: "Nauczysz się rozpoznawać ukryte mechanizmy psychologiczne, które sabotują Twoje działania." },
    { title: "System wdrożenia krok po kroku", text: "Otrzymasz konkretną strukturę pracy nad sobą — bez chaosu i zgadywania." },
    { title: "Materiały wdrożeniowe (Bonus)", text: "Trackery, checklisty i planery, które pomagają przełożyć wiedzę na realne działanie." }
  ];

  const targetAudience = [
    "Czujesz, że masz większy potencjał, ale trudno Ci przełożyć go na konkretne wyniki i systematyczne działanie.",
    "Zaczynasz z motywacją, ale brakuje ciągłości. Wracasz do starych schematów mimo dobrych chęci.",
    "Chcesz lepiej ogarniać pieniądze i decyzje, ale masz poczucie chaosu lub działania na autopilocie.",
    "Masz dość powierzchownej motywacji. Szukasz czegoś bardziej konkretnego i opartego na psychologii.",
    "Chcesz przejąć większą kontrolę nad sobą — nawykami, energią, koncentracją i kierunkiem, w którym zmierzasz."
  ];

  const bonusTools = [
    { icon: <Heart />, title: "Mapa Świadomej Komunikacji", desc: "Uporządkuj sposób, w jaki budujesz relacje, stawiasz granice i komunikujesz swoje potrzeby.", features: ["Klarowność w rozmowach", "Budowanie zaufania", "Lepsze stawianie granic"] },
    { icon: <Brain />, title: "Kompas Mechanizmów Psychologicznych", desc: "Naucz się szybciej rozpoznawać ukryte schematy wpływu i podejmować bardziej świadome decyzje.", features: ["Szybsze czytanie sytuacji", "Większa odporność psychiczna", "Świadome reakcje"] },
    { icon: <DollarSign />, title: "System Świadomych Decyzji Finansowych", desc: "Zyskaj większą kontrolę nad codziennymi wyborami i uporządkuj swoje podejście do pieniędzy.", features: ["Lepsze decyzje na co dzień", "Większa kontrola wydatków", "Myślenie długoterminowe"] },
    { icon: <Zap />, title: "Tracker Energii i Fokus", desc: "Monitoruj poziom energii, koncentracji i nawyków, które realnie wpływają na Twoją wydajność.", features: ["Świadome zarządzanie energią", "Lepszy fokus w ciągu dnia", "Budowanie stabilnej rutyny"] }
  ];

  const faqItems = [
    { q: "W jakim formacie otrzymam materiały?", a: "E-booki otrzymasz w formacie PDF, a audiobooki w formacie MP3 (zależnie od wybranego wariantu) — wraz z praktycznymi narzędziami do wdrożenia. Całość została przygotowana tak, aby wygodnie korzystać z niej na każdym urządzeniu — na telefonie, tablecie lub komputerze." },
    { q: "Kiedy otrzymam dostęp?", a: "Dostęp otrzymasz automatycznie po zaksięgowaniu płatności (zwykle w ciągu kilkunastu sekund). Na podany adres e-mail otrzymasz wiadomość z linkiem do pobrania e-booka. Możesz zacząć czytać i działać natychmiast." },
    { q: "Czy ten system sprawdzi się u mnie?", a: "System opiera się na uniwersalnych mechanizmach psychologii i pracy z nawykami, które są wspólne dla większości osób. Został zaprojektowany tak, aby można go było dopasować do własnej sytuacji — niezależnie od tego, czy pracujesz na etacie, prowadzisz firmę czy się uczysz." },
    { q: "Ile czasu zajmuje wdrożenie?", a: "Z materiałami możesz zapoznać się nawet w jeden wieczór, ale największą wartość daje stopniowe wdrażanie elementów systemu w codziennym działaniu. Pierwsze zmiany większość osób zauważa bardzo szybko — często już w pierwszych dniach pracy z systemem." }
  ];

  // Section render functions
  const renderHero = () => (
    <section className="section-padding pt-32 md:pt-40" data-testid="hero-section">
      <div className="container-custom">
        <div className="grid lg:grid-cols-2 gap-12 items-center">
          <div className="animate-fade-in-up">
            <span className="badge mb-6 inline-block" style={{ borderColor: getAccent('hero') + '80', color: getAccent('hero') }} data-testid="hero-badge">{getCMS('hero', 'content', {})?.badge || 'NOWOŚĆ 2026'}</span>
            <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold text-white leading-tight mb-6" data-testid="hero-title">
              {(() => {
                const title = getCMS('hero', 'title', 'Przejmij kontrolę nad swoim życiem. Raz na zawsze.');
                const green = 'Raz na zawsze.';
                const idx = title.lastIndexOf(green);
                if (idx === -1) return title;
                return <>{title.slice(0, idx)}<span style={{ color: getAccent('hero') }}>{green}</span></>;
              })()}
              {getCMS('hero', 'content', {})?.title_accent && <><span> </span><span style={{ color: getAccent('hero') }}>{getCMS('hero', 'content', {}).title_accent}</span></>}
            </h1>
            <p className="text-lg text-[#9CA3AF] mb-8 max-w-xl" data-testid="hero-subtitle">
              {getCMS('hero', 'subtitle', 'Sprawdzony system psychologiczny, który pomaga odzyskać fokus, pewność siebie, kontrolę nad pieniędzmi i relacjami — oparty na sprawdzonych zasadach psychologii i zachowań ludzi.')}
            </p>
            <div className="flex flex-col sm:flex-row gap-4 mb-6">
              <button onClick={scrollToProducts} className="btn-primary" data-testid="hero-cta-primary">
                {getCMS('hero', 'content', {})?.cta_primary || 'Zdobądź system + bonusy →'}
              </button>
              <button onClick={() => document.getElementById('solution')?.scrollIntoView({ behavior: 'smooth' })} className="btn-ghost" data-testid="hero-cta-secondary">
                {getCMS('hero', 'content', {})?.cta_secondary || 'Dowiedz się więcej'}
              </button>
            </div>
            {/* Social Media - Horizontal */}
            {socialMedia.length > 0 && (
              <div className="flex items-center gap-4" data-testid="hero-social">
                <span className="text-[#6B7280] text-sm">Obserwuj nas:</span>
                <div className="flex items-center gap-3">
                  {socialMedia.map((social) => (
                    <a
                      key={social.id}
                      href={social.url || '#'}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="w-9 h-9 rounded-full bg-[#10b981]/10 border border-[#10b981]/30 flex items-center justify-center text-[#10b981] hover:bg-[#10b981]/20 hover:scale-110 transition-all"
                      data-testid={`hero-social-${social.platform.toLowerCase()}`}
                    >
                      <SocialIcon platform={social.platform} size={18} />
                    </a>
                  ))}
                </div>
              </div>
            )}
          </div>
          <div className="animate-fade-in-up delay-200 flex justify-center lg:justify-end">
            <div className="relative w-full max-w-sm lg:max-w-md">
              <div className="absolute inset-0 bg-[#10b981]/20 blur-3xl rounded-full"></div>
              <img src={HERO_IMAGE} alt="MrMinds System" className="relative z-10 w-full rounded-2xl" data-testid="hero-image" />
            </div>
          </div>
        </div>
      </div>
    </section>
  );

  const renderProblem = () => (
    <section id="problem" className="section-padding" data-testid="problem-section">
      <div className="container-custom">
        <div className="text-center mb-16 animate-fade-in-up">
          <h2 className={`${getTitleSize('problem')} font-bold text-white mb-4`}>{getCMS('problem', 'title', 'Dlaczego czujesz, że stoisz w miejscu?')}</h2>
          <p className="text-[#9CA3AF] max-w-2xl mx-auto">
            {getCMS('problem', 'subtitle', 'To rzadko kwestia lenistwa. Najczęściej problem leży głębiej — w mechanizmach psychologicznych, nawykach i ukrytych blokadach, które sabotują Twoje działania.')}
          </p>
        </div>
        <div className="grid md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-5 gap-6">
          {(getCMS('problem', 'content', {})?.cards || problemCards).map((card, i) => (
            <div key={i} className="glass-card card-glow p-6 animate-fade-in-up" style={{ animationDelay: `${i * 100}ms` }} data-testid={`problem-card-${i}`}>
              <div className="w-12 h-12 rounded-xl flex items-center justify-center mb-4" style={{ backgroundColor: getAccent('problem') + '1a', color: getAccent('problem') }}>
                {problemCards[i]?.icon || <Zap size={24} />}
              </div>
              <h3 className="text-lg font-semibold text-white mb-2">{card.title}</h3>
              <p className="text-[#9CA3AF] text-sm">{card.text}</p>
            </div>
          ))}
        </div>
        <p className="text-center text-[#6B7280] mt-8 italic">{getCMS('problem', 'content', {})?.footer_text || 'Większość ludzi próbuje walczyć z objawami — zamiast usunąć przyczynę.'}</p>
      </div>
    </section>
  );

  const renderSolution = () => (
    <section id="solution" className="section-padding bg-gradient-to-b from-[#050505] to-[#0B0F0E]/30" data-testid="solution-section">
      <div className="container-custom">
        <div className="grid lg:grid-cols-2 gap-12 items-center">
          <div className="glass-card p-6 rounded-2xl glow-green">
            <div className="bg-[#050505] rounded-xl p-6 border border-[#10b981]/20">
              <div className="flex items-center gap-2 text-[#10b981] text-sm mb-6">
                <span className="w-2 h-2 bg-[#10b981] rounded-full animate-pulse"></span>
                TWÓJ POSTĘP
              </div>

              {/* Timeline comparison */}
              <div className="space-y-6">
                {/* Without system */}
                <div className="relative">
                  <div className="flex items-center gap-3 mb-2">
                    <div className="w-8 h-8 rounded-full bg-[#6B7280]/20 flex items-center justify-center">
                      <span className="text-[#6B7280] text-xs">😐</span>
                    </div>
                    <span className="text-[#6B7280] text-sm font-medium">Samodzielnie</span>
                  </div>
                  <div className="ml-11 space-y-2">
                    <div className="flex items-center gap-2">
                      <div className="h-1.5 w-16 bg-[#6B7280]/30 rounded-full"></div>
                      <span className="text-xs text-[#6B7280]">Miesiąc 1-3</span>
                    </div>
                    <p className="text-xs text-[#6B7280]">Próbujesz, rezygnujesz, wracasz do starych nawyków</p>
                  </div>
                </div>

                {/* Divider */}
                <div className="border-t border-[#1a1a1a] border-dashed"></div>

                {/* With system */}
                <div className="relative">
                  <div className="flex items-center gap-3 mb-2">
                    <div className="w-8 h-8 rounded-full bg-[#10b981]/20 flex items-center justify-center flex-shrink-0">
                      <span className="text-[#10b981] text-xs">💪</span>
                    </div>
                    <span className="text-[#10b981] text-sm font-medium">Z systemem MrMinds</span>
                  </div>
                  <div className="ml-11 space-y-2">
                    <div className="flex items-center gap-2 flex-wrap sm:flex-nowrap">
                      <div className="h-1.5 bg-gradient-to-r from-[#10b981] to-[#34d399] rounded-full w-24 sm:w-[140px] flex-shrink-0"></div>
                      <span className="text-xs text-[#10b981] whitespace-nowrap">Tydzień 1-4</span>
                    </div>
                    <p className="text-xs text-[#9CA3AF]">Konkretny plan → wdrożenie → widoczne efekty</p>
                  </div>
                </div>
              </div>

              <div className="mt-6 bg-[#10b981]/10 rounded-lg p-4 border border-[#10b981]/20">
                <div className="flex items-center gap-2">
                  <Target className="text-[#10b981]" size={18} />
                  <span className="text-sm text-[#9CA3AF]">RÓŻNICA</span>
                </div>
                <p className="text-base font-medium text-[#10b981] mt-1">Struktura zamiast chaosu</p>
                <p className="text-xs text-[#6B7280] mt-1">System, który prowadzi Cię krok po kroku</p>
              </div>
            </div>
          </div>
          <div>
            <h2 className="text-3xl md:text-4xl font-bold text-white mb-4">Co da Ci ten system?</h2>
            <p className="text-[#9CA3AF] mb-8">
              To nie jest kolejna dawka motywacji. To uporządkowany system psychologiczny, który pomaga odzyskać kontrolę nad działaniem, pieniędzmi, energią i decyzjami.
            </p>
            <div className="space-y-4">
              {solutionBullets.map((bullet, i) => (
                <div key={i} className="flex items-start gap-3" data-testid={`solution-bullet-${i}`}>
                  <Check className="text-[#10b981] mt-1 flex-shrink-0" size={20} />
                  <div>
                    <span className="text-white font-medium">{bullet.title}</span>
                    <span className="text-[#9CA3AF]"> – {bullet.text}</span>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
    </section>
  );

  const renderProducts = () => (
    <section id="produkty" className="section-padding overflow-x-hidden" data-testid="products-section">
      <div className="container-custom">
        {/* Autumn promo banner */}
        <div className="max-w-3xl mx-auto mb-10 rounded-2xl border-2 px-6 py-5 flex items-center justify-center gap-4 sm:gap-6" style={{ borderColor: '#f59e0b', background: 'linear-gradient(180deg, rgba(245,158,11,0.14), rgba(245,158,11,0.05))', boxShadow: '0 8px 30px rgba(245,158,11,0.14)' }}>
          <span className="text-7xl sm:text-8xl leading-none flex-shrink-0" aria-hidden="true">🍂</span>
          <div className="text-center min-w-0">
            <div className="font-black text-2xl sm:text-3xl tracking-wide" style={{ color: '#f59e0b' }}>JESIENNA PROMOCJA</div>
            <div className="text-sm mt-1.5" style={{ color: '#fcd34d' }}>Obniżone ceny — skorzystaj już teraz</div>
          </div>
        </div>
        <div className="text-center mb-16">
          <h2 className={`${getTitleSize('products')} font-bold text-white mb-4`}>{getCMS('products', 'title', 'Wybierz materiał dopasowany do Twojego celu')}</h2>
          <p className="text-[#9CA3AF] max-w-2xl mx-auto">
            {getCMS('products', 'subtitle', 'Każdy materiał rozwija inny obszar działania — możesz zacząć od jednego lub wybrać pełny pakiet.')}
          </p>
        </div>
        {loading ? (
          <div className="flex justify-center"><div className="spinner"></div></div>
        ) : (
          <div className="grid grid-cols-1 xl:grid-cols-2 gap-6 max-w-[88rem] mx-auto">
            {buildFamilies(products).map((group) => (
              <ProductFamilyCard key={group.family} group={group} />
            ))}
          </div>
        )}
      </div>
    </section>
  );

  const renderAudience = () => (
    <section className="section-padding bg-[#0B0F0E]/30" data-testid="audience-section">
      <div className="container-custom">
        <div className="text-center mb-12">
          <span className="badge mb-4 inline-block">DLA KOGO</span>
          <h2 className={`${getTitleSize('audience')} font-bold text-white mb-4`}>{getCMS('audience', 'title', 'Czy ten system jest dla Ciebie?')}</h2>
          <p className="text-[#9CA3AF] max-w-2xl mx-auto">
            Ten system został stworzony dla osób, które czują, że stać je na więcej — ale brakuje im spójnego planu i kontroli nad własnym działaniem.
          </p>
        </div>
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6 max-w-5xl mx-auto">
          {targetAudience.map((text, i) => (
            <div key={i} className="glass-card p-6" data-testid={`audience-card-${i}`}>
              <div className="w-8 h-8 rounded-lg bg-[#10b981]/10 flex items-center justify-center text-[#10b981] mb-4">
                <Users size={18} />
              </div>
              <p className="text-[#E5E7EB]">{text}</p>
            </div>
          ))}
        </div>
        <p className="text-center mt-8 font-medium" style={{ color: getAccent('audience') }}>
          Jeśli rozpoznajesz się w kilku punktach — ten system został stworzony właśnie dla Ciebie.
        </p>
      </div>
    </section>
  );

  const renderBonus = () => (
    <section className="section-padding" data-testid="bonus-section">
      <div className="container-custom">
        <div className="text-center mb-12">
          <span className="badge mb-4 inline-block bg-orange-500/20 border-orange-500/50 text-orange-400">🎁 WARTOŚĆ GRATISÓW: 147 PLN</span>
          <h2 className="text-3xl md:text-4xl font-bold text-white mb-2">{getCMS('bonus', 'title', 'Odbierz komplet narzędzi wdrożeniowych')}</h2>
          <h3 className="text-2xl font-bold text-[#10b981] mb-4">GRATIS</h3>
          <p className="text-[#9CA3AF] max-w-2xl mx-auto">
            Sama wiedza to za mało. Dlatego otrzymujesz gotowe narzędzia, które pomagają przełożyć system na codzienne działanie.
          </p>
        </div>
        <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
          {bonusTools.map((tool, i) => (
            <div key={i} className="glass-card card-glow p-6" data-testid={`bonus-card-${i}`}>
              <div className="w-14 h-14 rounded-2xl bg-gradient-to-br from-[#10b981]/20 to-[#10b981]/5 flex items-center justify-center text-[#10b981] mb-4 border border-[#10b981]/20">
                {tool.icon}
              </div>
              <h3 className="text-lg font-semibold text-white mb-2">{tool.title}</h3>
              <p className="text-[#9CA3AF] text-sm mb-4">{tool.desc}</p>
              <div className="space-y-2">
                {tool.features.map((f, j) => (
                  <div key={j} className="flex items-center gap-2 text-sm">
                    <Check size={14} className="text-[#10b981]" />
                    <span className="text-[#9CA3AF]">{f}</span>
                  </div>
                ))}
              </div>
            </div>
          ))}
        </div>
        <p className="text-center text-[#6B7280] mt-8 italic">To nie jest wiedza do przeczytania. To system do wdrożenia.</p>
      </div>
    </section>
  );

  const renderSystem = () => (
    <section className="section-padding bg-[#0B0F0E]/30" data-testid="system-section">
      <div className="container-custom">
        <div className="grid lg:grid-cols-2 gap-12 items-center">
          <div className="flex justify-center">
            <div className="relative">
              <div className="absolute inset-0 bg-[#10b981]/20 blur-3xl rounded-full"></div>
              <img src={getImage('system', 'main', SYSTEM_IMAGE_DEFAULT)} alt="System oparty na psychologii" className="relative z-10 w-64 h-64 md:w-80 md:h-80 rounded-full object-cover border-4 border-[#10b981]/30" data-testid="system-image" />
            </div>
          </div>
          <div>
            <h2 className="text-3xl md:text-4xl font-bold text-white mb-2">{getCMS('system', 'title', 'System oparty na realnej psychologii działania')}</h2>
            <p className="text-[#10b981] font-medium mb-6">{getCMS('system', 'subtitle', 'To nie jest teoria. To uporządkowany model pracy nad sobą.')}</p>
            <div className="text-[#9CA3AF] space-y-4 mb-8">
              <p>Ten system powstał w oparciu o kilkuletnią analizę psychologii działania, nawyków, podejmowania decyzji, dynamiki relacji oraz czynników wpływających na poziom energii i koncentracji.</p>
              <p>W procesie jego tworzenia przeanalizowano setki materiałów, badań i modeli psychologicznych — zarówno polskich, jak i zagranicznych — a także doświadczenia osób pracujących nad realną zmianą swoich wyników i stylu życia.</p>
              <p>Celem nie było stworzenie kolejnej dawki motywacji, lecz uporządkowanego systemu, który pomaga ograniczyć typowe błędy, odzyskać większą kontrolę nad sobą i wprowadzać zmiany w sposób bardziej świadomy i powtarzalny.</p>
            </div>
            <div className="flex flex-wrap gap-4">
              <div className="glass-card px-4 py-2 text-sm">
                <span className="text-[#10b981] font-medium">⭐ Kilkuletnia praca nad metodologią</span>
              </div>
              <div className="glass-card px-4 py-2 text-sm">
                <span className="text-[#10b981] font-medium">👥 1 000+ użytkowników systemu</span>
              </div>
              <div className="glass-card px-4 py-2 text-sm">
                <span className="text-[#10b981] font-medium">✓ Zweryfikowany w działaniu</span>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );

  const renderTestimonials = () => (
    <section id="opinie" className="section-padding" data-testid="testimonials-section">
      <div className="container-custom">
        <div className="text-center mb-12">
          <h2 className={`${getTitleSize('testimonials')} font-bold text-white mb-4`}>{getCMS('testimonials', 'title', 'Zobacz, co mówią użytkownicy systemu')}</h2>
        </div>
        <div className="grid md:grid-cols-3 gap-6">
          {testimonials.map((t, i) => (
            <div key={t.id || i} className="glass-card card-glow p-6" data-testid={`testimonial-${i}`}>
              <div className="flex gap-1 mb-4">
                {[...Array(5)].map((_, j) => (
                  <span key={j} style={{ color: j < (t.rating || 5) ? getAccent('testimonials') : '#4b5563' }}>★</span>
                ))}
              </div>
              <p className="text-[#E5E7EB] mb-6 italic">"{t.text}"</p>
              <div className="flex items-center gap-3">
                {t.image_url ? (
                  <img src={t.image_url} alt={t.name} className="w-10 h-10 rounded-full object-cover" />
                ) : (
                  <div className="w-10 h-10 rounded-full bg-gradient-to-br from-[#10b981]/30 to-[#0B0F0E] flex items-center justify-center text-white font-bold">
                    {t.name.charAt(0)}
                  </div>
                )}
                <div>
                  <p className="text-white font-medium">{t.name}</p>
                  <p className="text-[#6B7280] text-sm">{t.role}</p>
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );

  const renderFaq = () => (
    <section id="faq" className="section-padding bg-[#0B0F0E]/30" data-testid="faq-section">
      <div className="container-custom max-w-3xl">
        <div className="text-center mb-12">
          <h2 className={`${getTitleSize('faq')} font-bold text-white mb-4`}>{getCMS('faq', 'title', 'Częste pytania')}</h2>
        </div>
        <div className="space-y-4">
          {faqItems.map((item, i) => (
            <div key={i} className="glass-card overflow-hidden" data-testid={`faq-item-${i}`}>
              <button onClick={() => setOpenFaq(openFaq === i ? null : i)} className="w-full flex items-center justify-between p-6 text-left">
                <span className="text-white font-medium pr-4">{item.q}</span>
                {openFaq === i ? <ChevronUp style={{ color: getAccent('faq') }} className="flex-shrink-0" /> : <ChevronDown className="text-[#9CA3AF] flex-shrink-0" />}
              </button>
              <div className={`accordion-content ${openFaq === i ? 'open' : ''}`}>
                <div className="px-6 pb-6 text-[#9CA3AF]">{item.a}</div>
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );

  const renderCta = () => {
    const packs = products.filter(p => p.is_bundle);
    const pEbook = packs.find(p => (p.product_type || 'ebook') === 'ebook');
    const pAudio = packs.find(p => p.product_type === 'audiobook');
    const pVip = packs.find(p => p.product_type === 'premium');
    const buyPack = (p) => { addToCart(p); window.location.href = '/checkout'; };
    const pct = (p) => Math.round((1 - p.sale_price / p.regular_price) * 100);
    const packRow = (p, icon, title, sub, sub2, highlight) => p ? (
      <div className={`rounded-xl border p-4 sm:p-5 flex flex-col sm:flex-row sm:items-center gap-3 sm:gap-4 ${highlight ? 'border-[#f59e0b]/50 bg-[#f59e0b]/[0.06]' : 'border-white/10 bg-[#0B0F0E]'}`}>
        <div className="flex-1 min-w-0">
          <p className="text-white font-semibold flex items-center gap-2 flex-wrap">
            <span>{icon} {title}</span>
            {highlight && <span className="text-[10px] font-bold px-2 py-0.5 rounded-full" style={{ background: '#f59e0b', color: '#0B0F0E' }}>BESTSELLER</span>}
          </p>
          {sub && <p className="text-[#9CA3AF] text-sm mt-0.5">{sub}</p>}
          {sub2 && <p className="text-[#6B7280] text-xs mt-0.5">{sub2}</p>}
        </div>
        <div className="flex flex-col sm:flex-row sm:items-center gap-3 sm:gap-4 w-full sm:w-auto flex-shrink-0">
          <div className="flex items-center sm:flex-col sm:items-end gap-2 sm:gap-0 leading-tight">
            <div className="text-xs text-[#6B7280] line-through" style={{ textDecorationThickness: '1px' }}>{p.regular_price} zł</div>
            <div className="flex items-center gap-1.5">
              <span className="text-2xl font-bold" style={{ color: highlight ? '#f59e0b' : '#10b981' }}>{p.sale_price} zł</span>
              <span className="px-1.5 py-0.5 bg-red-500/20 text-red-400 text-[10px] rounded">-{pct(p)}%</span>
            </div>
          </div>
          <button onClick={() => buyPack(p)} className="btn-primary w-full sm:w-auto justify-center text-sm sm:text-base" style={highlight ? { background: '#f59e0b', borderColor: '#f59e0b', color: '#0B0F0E' } : {}} data-testid={`bundle-cta-${p.product_type || 'ebook'}`}>
            Kupuję i wdrażam system →
          </button>
        </div>
      </div>
    ) : null;
    return (
    <section className="section-padding" data-testid="cta-section">
      <div className="container-custom">
        <div className="glass-card glow-green p-8 md:p-12 max-w-4xl mx-auto text-center">
          <span className="badge mb-6 inline-block">OFERTA LIMITOWANA</span>
          <h2 className={`${getTitleSize('cta')} font-bold text-white mb-4`}>{getCMS('cta', 'title', 'Zainwestuj w').replace(/\s*siebie\.?\s*$/i, '')} <span style={{ color: getAccent('cta') }}>siebie</span>.</h2>
          <p className="text-[#9CA3AF] max-w-2xl mx-auto mb-8">
            {getCMS('cta', 'subtitle', 'Otrzymujesz kompletny pakiet materiałów obejmujący nawyki i działanie, mechanizmy wpływu i perswazji, relacje i komunikację, świadome decyzje finansowe oraz energię i koncentrację — wszystko w jednym spójnym systemie.')}
          </p>

          {(pEbook || pAudio || pVip) && (
            <div className="glass-card p-6 md:p-8 mb-8 text-left">
              <h3 className="text-lg font-semibold text-white mb-3">📦 W każdym pakiecie 5 tytułów:</h3>
              <div className="grid sm:grid-cols-2 gap-x-6 gap-y-1 text-sm text-[#9CA3AF] mb-6">
                <p>✔ Manipulacja — mechanizmy perswazji i wpływu</p>
                <p>✔ Wpływ — świadome podejmowanie decyzji</p>
                <p>✔ Relacje — budowanie silnej komunikacji</p>
                <p>✔ Pieniądze — lepsze decyzje finansowe</p>
                <p>✔ Testosteron — energia, fokus i wydajność</p>
              </div>

              <div className="space-y-3">
                {packRow(pEbook, '📕', 'Pakiet 5 e-booków (PDF)', '5 e-booków w formacie PDF', null, false)}
                {packRow(pAudio, '🎧', 'Pakiet 5 audiobooków (MP3)', '5 audiobooków w formacie MP3', null, false)}
                {packRow(pVip, '👑', 'VIP', '5 e-booków + 5 audiobooków', 'Wszystko razem: PDF + MP3', true)}
              </div>
              <p className="text-[#6B7280] text-sm mt-4 text-center">Jednorazowa płatność • Dożywotni dostęp</p>

              <h4 className="text-white font-medium mb-2 mt-6">🎁 Bonusy wdrożeniowe (gratis do każdego pakietu):</h4>
              <div className="grid sm:grid-cols-2 gap-x-6 gap-y-1 text-sm text-[#9CA3AF]">
                <p>✔ Tracker Nawyków</p>
                <p>✔ Tracker Zadań</p>
                <p>✔ Planner Tygodniowy</p>
                <p>✔ Tracker Energii i Fokus</p>
              </div>
            </div>
          )}

          <div className="flex flex-wrap justify-center gap-6 text-sm">
            <div className="trust-badge"><Shield className="text-[#10b981]" size={18} /> Bezpieczna płatność</div>
            <div className="trust-badge"><Clock className="text-[#10b981]" size={18} /> Dostęp natychmiast</div>
            <div className="trust-badge"><Award className="text-[#10b981]" size={18} /> Gwarancja satysfakcji</div>
          </div>
        </div>
      </div>
    </section>
    );
  };

  const SECTION_RENDERERS = {
    hero: renderHero,
    problem: renderProblem,
    solution: renderSolution,
    products: renderProducts,
    audience: renderAudience,
    bonus: renderBonus,
    system: renderSystem,
    testimonials: renderTestimonials,
    faq: renderFaq,
    cta: renderCta,
  };

  return (
    <div className="bg-[#050505] min-h-screen overflow-x-hidden">
      <Navbar />

      {cmsOrder.map(sectionId => {
        const renderer = SECTION_RENDERERS[sectionId];
        if (!renderer) return null;
        return (
          <div key={sectionId} style={{ '--section-accent': getAccent(sectionId) }}>
            {renderer()}
          </div>
        );
      })}

      <Footer />
    </div>
  );
};

export default LandingPage;
